import { uniq } from 'lodash';

import { DynamicAudience, SettingAdditionalAttendeeInput } from 'src/generated/mloop-graphql';

/**
 * Per-entry additional-attendee model for SETTINGS (org defaults and job-stage defaults).
 *
 * Distinct from the event model in `additionalAttendees.ts` on purpose: a setting has no
 * application to resolve an audience against, so here a dynamic audience is a *recipient* that
 * carries the role each of its members will inherit, not provenance on a concrete person. The
 * members get their own individually toggleable flags when the communications page materializes
 * them.
 */
export enum SettingAttendeeKind {
  Employee = 'EMPLOYEE',
  Email = 'EMAIL',
  Audience = 'AUDIENCE',
}

export type SettingAttendeeEntry =
  | { kind: SettingAttendeeKind.Employee; employeeId: string; isOptional: boolean }
  | { kind: SettingAttendeeKind.Email; email: string; isOptional: boolean }
  | { kind: SettingAttendeeKind.Audience; audience: DynamicAudience; isOptional: boolean };

/** Stable identity, shared with the chip action so a chip can name the entry it toggles. */
export const settingAttendeeKey = (entry: SettingAttendeeEntry): string => {
  switch (entry.kind) {
    case SettingAttendeeKind.Employee:
      return `employee:${entry.employeeId}`;
    case SettingAttendeeKind.Email:
      return `email:${entry.email.trim().toLowerCase()}`;
    default:
      return `audience:${entry.audience}`;
  }
};

type SettingAttendeeGraphql = {
  email?: string | null;
  employeeId?: string | null;
  dynamicAudience?: DynamicAudience | null;
  isOptional: boolean;
};

type LegacyPreference =
  | {
      employeeIds?: string[] | null;
      externalEmailAddresses?: string[] | null;
      dynamicAudiences?: DynamicAudience[] | null;
    }
  | null
  | undefined;

const fromGraphqlEntries = (entries: SettingAttendeeGraphql[]): SettingAttendeeEntry[] => {
  const parsed: SettingAttendeeEntry[] = [];
  entries.forEach((entry) => {
    const isOptional = Boolean(entry.isOptional);
    if (entry.employeeId) {
      parsed.push({ kind: SettingAttendeeKind.Employee, employeeId: entry.employeeId, isOptional });
      return;
    }
    if (entry.email) {
      parsed.push({ kind: SettingAttendeeKind.Email, email: entry.email, isOptional });
      return;
    }
    if (entry.dynamicAudience) {
      parsed.push({ kind: SettingAttendeeKind.Audience, audience: entry.dynamicAudience, isOptional });
    }
  });
  return parsed;
};

/**
 * The settings read rule, the same one the server applies: per-entry values win when present, and
 * a level still holding only the legacy recipient list materializes as uniform entries carrying
 * that level's single optional flag. There is no settings migration -- a document upgrades shape
 * when it is next saved.
 */
export const readSettingAttendees = (
  entries: SettingAttendeeGraphql[] | null | undefined,
  legacyPreference: LegacyPreference,
  legacyIsOptional: boolean | null | undefined
): SettingAttendeeEntry[] => {
  if (entries?.length) {
    return fromGraphqlEntries(entries);
  }

  const isOptional = Boolean(legacyIsOptional);
  return [
    ...uniq(legacyPreference?.employeeIds ?? []).map((employeeId) => ({
      kind: SettingAttendeeKind.Employee as const,
      employeeId,
      isOptional,
    })),
    ...uniq(legacyPreference?.externalEmailAddresses ?? []).map((email) => ({
      kind: SettingAttendeeKind.Email as const,
      email,
      isOptional,
    })),
    ...uniq(legacyPreference?.dynamicAudiences ?? []).map((audience) => ({
      kind: SettingAttendeeKind.Audience as const,
      audience,
      isOptional,
    })),
  ];
};

export const setSettingAttendeeOptional = (
  entries: SettingAttendeeEntry[],
  key: string,
  isOptional: boolean
): SettingAttendeeEntry[] =>
  entries.map((entry) => (settingAttendeeKey(entry) === key ? { ...entry, isOptional } : entry));

/** The legacy recipient list these entries still have to be written as, alongside the new one. */
export const toLegacyPreference = (
  entries: SettingAttendeeEntry[]
): { employeeIds: string[]; externalEmailAddresses: string[]; dynamicAudiences: DynamicAudience[] } => ({
  employeeIds: entries
    .filter((entry) => entry.kind === SettingAttendeeKind.Employee)
    .map((entry) => (entry as { employeeId: string }).employeeId),
  externalEmailAddresses: entries
    .filter((entry) => entry.kind === SettingAttendeeKind.Email)
    .map((entry) => (entry as { email: string }).email),
  dynamicAudiences: entries
    .filter((entry) => entry.kind === SettingAttendeeKind.Audience)
    .map((entry) => (entry as { audience: DynamicAudience }).audience),
});

/**
 * The collection-level flag the legacy pair still has to carry. True only when EVERY recipient is
 * optional: one boolean cannot express a mixed list, and defaulting to true would demote a
 * required recipient for any reader still on the legacy shape.
 */
export const toLegacyIsOptional = (entries: SettingAttendeeEntry[]): boolean =>
  entries.length > 0 && entries.every((entry) => entry.isOptional);

/** JSON for the `additionalAttendees` key inside an org-pref document. */
export const toOrgPrefEntriesJson = (
  entries: SettingAttendeeEntry[]
): { email?: string; employeeId?: string; dynamicAudience?: DynamicAudience; isOptional: boolean }[] =>
  entries.map((entry) => {
    switch (entry.kind) {
      case SettingAttendeeKind.Employee:
        return { employeeId: entry.employeeId, isOptional: entry.isOptional };
      case SettingAttendeeKind.Email:
        return { email: entry.email, isOptional: entry.isOptional };
      default:
        return { dynamicAudience: entry.audience, isOptional: entry.isOptional };
    }
  });

/** `SettingAdditionalAttendeeInput[]` for the job-stage upsert. */
export const toSettingAttendeeInputs = (entries: SettingAttendeeEntry[]): SettingAdditionalAttendeeInput[] =>
  toOrgPrefEntriesJson(entries) as SettingAdditionalAttendeeInput[];

export const isSameSettingAttendees = (left: SettingAttendeeEntry[], right: SettingAttendeeEntry[]): boolean => {
  const fingerprint = (entries: SettingAttendeeEntry[]) =>
    entries
      .map((entry) => `${settingAttendeeKey(entry)}|${entry.isOptional}`)
      .sort()
      .join(',');
  return fingerprint(left) === fingerprint(right);
};

/** The selector's view of a settings list. Audiences are recipients here, not provenance. */
export const toSettingSelectorValue = (
  entries: SettingAttendeeEntry[]
): { employeeIds: string[]; externalEmailAddresses: string[]; dynamicAudiences: DynamicAudience[] } =>
  toLegacyPreference(entries);

/**
 * Rebuilds a settings list after a selector change. Roles already chosen survive; a newly picked
 * recipient starts OPTIONAL, the same rule the communications surfaces use.
 *
 * No flag guard here: with the per-entry flag off the dialog's roles never reach the wire -- the
 * save omits the entries and passes the stored collection-level boolean straight back -- so this
 * default is only ever read in the per-recipient contract.
 */
export const applySettingSelectorChange = (
  current: SettingAttendeeEntry[],
  selection: { selectedEmployeeIds: string[]; selectedEmailAddresses: string[]; dynamicAudiences: DynamicAudience[] }
): SettingAttendeeEntry[] => {
  const previous = new Map(current.map((entry) => [settingAttendeeKey(entry), entry]));
  const roleFor = (key: string) => previous.get(key)?.isOptional ?? true;

  return [
    ...uniq(selection.selectedEmployeeIds).map((employeeId) => ({
      kind: SettingAttendeeKind.Employee as const,
      employeeId,
      isOptional: roleFor(`employee:${employeeId}`),
    })),
    ...uniq(selection.selectedEmailAddresses).map((email) => ({
      kind: SettingAttendeeKind.Email as const,
      email,
      isOptional: roleFor(`email:${email.trim().toLowerCase()}`),
    })),
    ...uniq(selection.dynamicAudiences).map((audience) => ({
      kind: SettingAttendeeKind.Audience as const,
      audience,
      isOptional: roleFor(`audience:${audience}`),
    })),
  ];
};

export const hasOptionalSettingAttendee = (entries: SettingAttendeeEntry[]): boolean =>
  entries.some((entry) => entry.isOptional);
